import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { LOGO } from "../lib/api";

const NAV = [
  { label: "Shop", to: "/shop" },
  { label: "Our Story", to: "/our-story" },
  { label: "Botanicals", to: "/botanicals" },
  { label: "Global Markets", to: "/global-markets" },
  { label: "Journal", to: "/journal" },
  { label: "Contact", to: "/contact" },
];

const PARTNER = [
  { label: "Become a Distributor", to: "/become-a-distributor" },
  { label: "Wholesale", to: "/wholesale" },
  { label: "Export Enquiry", to: "/export-enquiry" },
  { label: "International Buyers", to: "/international-buyers" },
  { label: "Private Label — Coming Soon", to: "/private-label" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); setDropdown(false); }, [loc.pathname]);

  return (
    <>
      <div className="bg-botanical text-ivory text-center py-2 text-[11px] tracking-[0.2em] uppercase font-medium" data-testid="announcement-bar">
        India-Origin • Premium Botanicals • Building Global Partnerships
      </div>
      <header
        className={`sticky top-0 z-50 transition-all duration-500 ${scrolled ? "bg-ivory/80 backdrop-blur-xl border-b border-border" : "bg-ivory/40 backdrop-blur-md"}`}
        data-testid="main-header"
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 flex items-center justify-between h-32">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img src={LOGO} alt="Cultiva Botanics — Pure Plants. Better Living." className="h-24 sm:h-28 w-auto object-contain" style={{ mixBlendMode: "multiply" }} />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className="link-underline text-sm text-botanical/90 hover:text-botanical font-medium" data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>
                {n.label}
              </Link>
            ))}
            <div className="relative" onMouseEnter={() => setDropdown(true)} onMouseLeave={() => setDropdown(false)}>
              <Link to="/partner" className="inline-flex items-center gap-1.5 rounded-full bg-botanical text-ivory px-6 py-2.5 text-sm font-medium hover:bg-forest transition-colors" data-testid="partner-with-us-btn">
                Partner With Us <ChevronDown className="h-3.5 w-3.5" />
              </Link>
              <AnimatePresence>
                {dropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.25 }}
                    className="absolute right-0 top-full pt-3 w-72"
                  >
                    <div className="bg-ivory border border-border rounded-md overflow-hidden shadow-xl shadow-botanical/5">
                      {PARTNER.map((p) => (
                        <Link key={p.to} to={p.to} className="block px-5 py-3.5 text-sm text-botanical/80 hover:bg-muted hover:text-botanical transition-colors border-b border-border last:border-0" data-testid={`partner-dd-${p.to.slice(1)}`}>
                          {p.label}
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </nav>

          <button className="lg:hidden text-botanical" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="lg:hidden overflow-hidden bg-ivory border-t border-border">
              <div className="px-6 py-4 flex flex-col">
                {NAV.map((n) => (
                  <Link key={n.to} to={n.to} className="py-3 text-botanical border-b border-border font-medium" data-testid={`m-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>{n.label}</Link>
                ))}
                <div className="pt-3 overline">Partner With Us</div>
                {PARTNER.map((p) => (
                  <Link key={p.to} to={p.to} className="py-2.5 text-sm text-botanical/80" data-testid={`m-partner-${p.to.slice(1)}`}>{p.label}</Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}
